import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase, formatAuthError } from '@/lib/supabase';
import { useSubscriptionStore } from '@/store/subscription';
import { track, identifyUser } from '@/lib/posthog';
import { Info } from 'lucide-react';

type LoginState = 'email' | 'otp';
type HintType = 'existing' | null;

export function LoginForm() {
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get('email') || '';
  const hint = searchParams.get('hint') as HintType;

  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [state, setState] = useState<LoginState>('email');
  const navigate = useNavigate();
  const { fetchSubscription } = useSubscriptionStore();

  // Get hint message based on URL param
  const getHintMessage = () => {
    switch (hint) {
      case 'existing':
        return 'You already have an account. Sign in below.';
      default:
        return null;
    }
  };
  const hintMessage = getHintMessage();

  const checkUserStatus = async (email: string): Promise<{ exists: boolean; confirmed: boolean }> => {
    try {
      const response = await supabase.functions.invoke('check-user-exists', {
        body: { email },
      });
      return {
        exists: response.data?.exists ?? false,
        confirmed: response.data?.confirmed ?? false,
      };
    } catch (err) {
      console.error('Error checking user status:', err);
      // If we can't check, assume user might exist and is confirmed
      return { exists: true, confirmed: true };
    }
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Check if user exists and if their email is confirmed
      const { exists, confirmed } = await checkUserStatus(email);

      // User doesn't exist - redirect to signup
      if (!exists) {
        navigate(`/signup?email=${encodeURIComponent(email)}&hint=new`);
        return;
      }

      // User exists but hasn't confirmed email - redirect to signup
      if (!confirmed) {
        navigate(`/signup?email=${encodeURIComponent(email)}&hint=unconfirmed`);
        return;
      }

      // User exists and confirmed - send OTP using signInWithOtp
      // This triggers the "Magic Link" email template
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: `${window.location.origin}/home`,
        },
      });

      if (otpError) {
        throw otpError;
      }

      setState('otp');
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'email',
      });

      if (verifyError) throw verifyError;

      identifyUser(email, { email });
      track('login_completed', { email });

      // Check subscription status after login
      await fetchSubscription();
      const { subscription } = useSubscriptionStore.getState();

      // Edge case: user signed up but never completed Stripe checkout
      // Redirect to signup page which shows the "no subscription" state with options
      if (!subscription) {
        console.log('No subscription found, redirecting to signup for subscription setup');
        navigate('/signup');
        return;
      }

      // Navigate to dashboard on success
      navigate('/home');
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setIsLoading(false);
    }
  };

  // OTP verification state
  if (state === 'otp') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-teal-600">
            Check Your Email
          </CardTitle>
          <CardDescription>
            We sent a sign-in code to {email}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="text-center text-sm text-muted-foreground">
              <ul className="space-y-1 text-left max-w-xs mx-auto">
                <li>• A 6-digit code you can enter below</li>
                <li>• A magic link you can click to sign in</li>
              </ul>
            </div>

            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="otp" className="text-sm font-medium">
                  Enter your code
                </label>
                <Input
                  id="otp"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="text-center text-lg tracking-widest"
                  autoFocus
                />
              </div>
              {error && (
                <div className="text-red-600 text-sm text-center">{error}</div>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || otp.length !== 6}
              >
                {isLoading ? 'Verifying...' : 'Sign In'}
              </Button>
            </form>

            <div className="pt-4 border-t space-y-3">
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setState('email');
                  setOtp('');
                  setError('');
                }}
              >
                Use a different email
              </Button>
              <p className="text-sm text-muted-foreground text-center">
                Don't have an account?{' '}
                <button
                  onClick={() => navigate('/signup')}
                  className="text-teal-600 hover:underline"
                >
                  Sign up
                </button>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Initial state - enter email
  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl font-bold text-teal-600">
          Welcome Back
        </CardTitle>
        <CardDescription>
          Sign in to your account
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Hint banner when redirected from signup */}
        {hintMessage && (
          <div className="mb-4 p-3 rounded-lg flex items-start gap-2 bg-blue-50 border border-blue-200">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
            <p className="text-sm text-blue-700">
              {hintMessage}
            </p>
          </div>
        )}

        <form onSubmit={handleSendCode} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus={!initialEmail}
            />
          </div>
          {error && (
            <div className="text-red-600 text-sm text-center">{error}</div>
          )}
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? 'Checking...' : 'Continue'}
          </Button>
        </form>
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Don't have an account?{' '}
            <button
              onClick={() => navigate('/signup')}
              className="text-teal-600 hover:underline"
            >
              Sign up
            </button>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
